import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'

import { classNames } from 'primereact/utils'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { InputNumber } from 'primereact/inputnumber'
import { Toast } from 'primereact/toast'
import { MultiSelect } from 'primereact/multiselect'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs from 'dayjs'
import { Checkbox } from 'primereact/checkbox'

import server from '../../../server'

import { ToastPopUp } from '../../shared/utils'

// Manufacturer exclusivity. 'public' keeps the historical behaviour; the other
// two restrict who may see the manufacturer and are paired with the pharmacy /
// pharmacy group pickers below.
const VISIBILITY_OPTIONS = [
  { label: 'Available to all pharmacies', value: 'public' },
  { label: 'Exclusive to selected pharmacies', value: 'exclusive' },
  {
    label: 'Not connectable (Origins Management only)',
    value: 'restricted',
  },
]

const AddEditManufacturer = forwardRef((props, ref) => {
  const { AddEditRef } = ref
  const { manufacturers, setManufacturers, emptyManufacturer } = props
  const [manufactureDialog, setManufactureDialog] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [isManufactureEdit, setIsManufactureEdit] = useState(false)

  const [manufacturer, setManufacturer] = useState([])
  const [manufacturerData, setManufacturerData] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedSystems, setSelectedSystems] = useState(['Origins Direct'])

  // Exclusivity: who is allowed to see / connect to this manufacturer.
  const [visibility, setVisibility] = useState('public')
  const [pharmacyOptions, setPharmacyOptions] = useState([])
  const [pharmacyGroupOptions, setPharmacyGroupOptions] = useState([])
  const [selectedPharmacies, setSelectedPharmacies] = useState([])
  const [selectedPharmacyGroups, setSelectedPharmacyGroups] = useState([])
  const [accessLoading, setAccessLoading] = useState(false)

  const toast = useRef(null)
  useImperativeHandle(AddEditRef, () => ({
    openNew() {
      setSubmitted(false)
      setManufactureDialog(true)
      setManufacturers(emptyManufacturer)
      setVisibility('public')
      setSelectedPharmacies([])
      setSelectedPharmacyGroups([])
    },

    editManufacturer(manufacturer) {
      setSubmitted(false)
      setIsManufactureEdit(true)

      const parsedCutOffTime =
        typeof manufacturer.cutOffTime === 'string'
          ? dayjs(manufacturer.cutOffTime, 'h:mm A').toDate()
          : manufacturer.cutOffTime

      setManufacturers({
        ...manufacturer,

        cutOffTime: parsedCutOffTime,
      })
      setVisibility(manufacturer.visibility || 'public')
      setSelectedPharmacies([])
      setSelectedPharmacyGroups([])
      getAccess(manufacturer.id)
      setManufactureDialog(true)
    },
  }))

  const hideDialog = () => {
    setManufactureDialog(false)
    setIsManufactureEdit(false)
    setSubmitted(false)

    // Reset all states
    setManufacturers(emptyManufacturer) // Reset the manufacturer details to the initial state
    setManufacturerData([]) // Clear manufacturer data options
    setManufacturer([]) // Clear manufacturer list
    setLoading(false) // Reset loading state
    setVisibility('public')
    setSelectedPharmacies([])
    setSelectedPharmacyGroups([])
    setAccessLoading(false)
  }

  // const hideDialog = () => {
  //   setManufactureDialog(false)
  //   setIsManufactureEdit(false)
  // }

  // Gets table data
  const getData = () => {
    const requestParams = `?ordable=false`

    server
      .get(`/manufacturers/${requestParams}`)
      .then((response) => {
        const manufacturer = response?.data?.Manufacturers
        setManufacturer(manufacturer)
      })
      .catch((error) => {
        // setTableLoading(false)
        // setError(error.response.data.message)
      })
  }

  // Pharmacies and pharmacy groups an exclusive manufacturer can be granted to
  const getAccessOptions = () => {
    server
      .get('/manufacturers/access/options')
      .then((response) => {
        setPharmacyOptions(response?.data?.pharmacies || [])
        setPharmacyGroupOptions(response?.data?.pharmacyGroups || [])
      })
      .catch((error) => {
        // Leave the pickers empty; saving still works for 'public' visibility.
      })
  }

  // Current grants for the manufacturer being edited
  const getAccess = (manufacturerId) => {
    if (!manufacturerId) return

    setAccessLoading(true)
    server
      .get(`/manufacturers/${manufacturerId}/access`)
      .then((response) => {
        setSelectedPharmacies(response?.data?.pharmacyIds || [])
        setSelectedPharmacyGroups(response?.data?.pharmacyGroupIds || [])
        setVisibility(response?.data?.visibility || 'public')
        setAccessLoading(false)
      })
      .catch((error) => {
        setAccessLoading(false)
      })
  }

  useEffect(() => {
    if (manufactureDialog === true) {
      getData()
      getAccessOptions()
    }
  }, [manufactureDialog])

  useEffect(() => {
    if (manufacturer) {
      const options = manufacturer.map((item) => ({
        label: item.name,
        value: item.id,
      }))
      setManufacturerData(options)
    }
  }, [manufacturer])
  const onInputNumberChange = (e, name) => {
    const val = e.value || 0
    let _manufacturers = { ...manufacturers }
    if (name === 'leadTime') {
      _manufacturers[name] = Math.min(val, 10)
    } else {
      _manufacturers[name] = val
    }

    setManufacturers(_manufacturers)
  }
  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || ''
    let _manufacturers = { ...manufacturers }
    _manufacturers[`${name}`] = val

    setManufacturers(_manufacturers)
  }

  const onSystemChange = (e) => {
    let _selectedSystems = [...selectedSystems]
    if (e.checked) _selectedSystems.push(e.value)
    else _selectedSystems = _selectedSystems.filter((val) => val !== e.value)
    setSelectedSystems(_selectedSystems)
  }

  const saveManufactures = () => {
    const emailDomainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/ // Basic domain validation pattern

    if (
      manufacturers.id &&
      manufacturers.gpIsOrdable &&
      manufacturers.email &&
      manufacturers.cutOffTime &&
      manufacturers.leadTime
    ) {
      // Validate email domain only if provided
      if (manufacturers.emailDomain && !emailDomainPattern.test(manufacturers.emailDomain)) {
        ToastPopUp(
          toast,
          'error',
          'Validation Error',
          'Invalid domain format, should be similar to domain.com'
        )
        return
      }

      setLoading(true)
      const formattedCutOffTime = manufacturers.cutOffTime
        ? new Date(manufacturers.cutOffTime).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : null

      const dataToSend = {
        gpMinOrderPrice: manufacturers.gpMinOrderPrice,
        comingSoon: manufacturers.comingSoon,
        gpIsOrdable: true,
        email: manufacturers.email,
        leadTime: manufacturers.leadTime,
        cutOffTime: formattedCutOffTime,
        emailDomain: manufacturers.emailDomain,
        shippingFee: manufacturers.shippingFee,
        odPaAdvantage: manufacturers.odPaAdvantage || 0,
        system: selectedSystems,
        disableRequestNewAccount: !!manufacturers.disableRequestNewAccount,
        visibility,
      }

      server
        .patch(`/manufacturers/${manufacturers.id}`, dataToSend)
        .then(() =>
          // Grants only mean something for a non-public manufacturer; going
          // back to public clears them server-side.
          visibility === 'public'
            ? Promise.resolve()
            : server.put(
                `/manufacturers/${manufacturers.id}/access`,
                {
                  pharmacyIds: selectedPharmacies,
                  pharmacyGroupIds: selectedPharmacyGroups,
                },
                {
                }
              )
        )
        .then((response) => {
          props.getData()
          if (isManufactureEdit) {
            setLoading(false)
            setManufactureDialog(false)

            ToastPopUp(
              toast,
              'success',
              'Manufacturers Updated',
              'Manufacturers has been Updated successfully.'
            )
          } else {
            setLoading(false)
            setManufactureDialog(false)
            ToastPopUp(
              toast,
              'success',
              'Manufacturers Added',
              'Manufacturers has been added successfully.'
            )
          }
          setIsManufactureEdit(false)
          setSubmitted(true)
        })
        .catch((e) => {
          ToastPopUp(
            toast,
            'error',
            'Error',
            e?.response?.data?.message || 'Something went wrong.'
          )
          setIsManufactureEdit(false)
          setSubmitted(true)
          setLoading(false)
        })
    } else {
      setSubmitted(true)
      ToastPopUp(
        toast,
        'error',
        'Validation Error',
        'Please fill all required fields.'
      )
    }
  }

  //  Dialog footers
  const manufactureDialgeFooter = (
    <React.Fragment>
      <Button label='Cancel' icon='pi pi-times' outlined onClick={hideDialog} />
      <Button
        label={
          isManufactureEdit ? 'Update Manufacturer' : 'Add New Manufacturer'
        }
        loading={loading}
        icon='pi pi-check'
        onClick={saveManufactures}
      />
    </React.Fragment>
  )
  const statusOptions = [
    { label: 'Coming Soon', value: 'true' },
    { label: 'Available', value: 'false' },
  ]

  return (
    <>
      <div className='card flex justify-content-center'>
        <Toast ref={toast} />
      </div>
      <Dialog
        visible={manufactureDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header={
          isManufactureEdit ? 'Update Manufacturer' : 'Add New Manufacturer'
        }
        modal
        className='p-fluid'
        footer={manufactureDialgeFooter}
        // onHide={() => setManufactureDialog(false)}
        onHide={hideDialog}
      >
        <div className='field mt-4'>
          <label htmlFor='ManufacturerName' className='font-bold'>
            Manufacturer Name
          </label>
          <Dropdown
            id='ManufacturerName'
            value={manufacturers.id}
            required={!isManufactureEdit}
            onChange={(e) => onInputChange(e, 'id')}
            options={manufacturerData}
            optionLabel='label'
            placeholder={
              isManufactureEdit
                ? `${manufacturers.name}`
                : 'Select Manufacturer'
            }
            filter
            // className='w-full md:w-14rem'
            readOnly={isManufactureEdit ? true : false}
            className={classNames(
              `w-full md:w-14rem ${isManufactureEdit && 'disabledInput'}`
            )}
          />
          {submitted && !manufacturers.id && (
            <small className='p-error'>Manufacturer name is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='ManufacturerName' className='font-bold'>
            Status
          </label>
          <Dropdown
            id='status'
            value={manufacturers?.comingSoon}
            required={!isManufactureEdit}
            onChange={(e) => onInputChange(e, 'comingSoon')}
            options={statusOptions}
            optionLabel='label'
            placeholder={
              isManufactureEdit
                ? manufacturers.comingSoon
                  ? 'Coming Soon'
                  : 'Available'
                : 'Select Status'
            }
            filter
            className={classNames(`w-full md:w-14rem`)}
          />
          {submitted && !manufacturers.id && (
            <small className='p-error'>Manufacturer name is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='visibility' className='font-bold'>
            Availability
          </label>
          <Dropdown
            id='visibility'
            value={visibility}
            onChange={(e) => setVisibility(e.value)}
            options={VISIBILITY_OPTIONS}
            optionLabel='label'
            placeholder='Select Availability'
            className={classNames(`w-full md:w-14rem`)}
          />
          <small className='p-text-secondary'>
            {visibility === 'exclusive'
              ? 'Only the pharmacies and groups selected below can see, connect to and order from this manufacturer.'
              : visibility === 'restricted'
              ? 'Nobody can connect or order on Origins Direct. The PA Navigator still lists this manufacturer for every Origins Management user, admin, and group member.'
              : 'Every pharmacy can see, connect to and order from this manufacturer.'}
          </small>
        </div>

        {visibility !== 'public' && (
          <>
            <div className='field mt-4'>
              <label htmlFor='accessPharmacies' className='font-bold'>
                Enabled Pharmacies
              </label>
              <MultiSelect
                inputId='accessPharmacies'
                value={selectedPharmacies}
                onChange={(e) => setSelectedPharmacies(e.value)}
                options={pharmacyOptions}
                optionLabel='name'
                optionValue='id'
                filter
                disabled={accessLoading}
                placeholder='Select Pharmacies'
                maxSelectedLabels={3}
                style={{ width: '100%' }}
              />
            </div>

            <div className='field mt-4'>
              <label htmlFor='accessPharmacyGroups' className='font-bold'>
                Enabled Pharmacy Groups
              </label>
              <MultiSelect
                inputId='accessPharmacyGroups'
                value={selectedPharmacyGroups}
                onChange={(e) => setSelectedPharmacyGroups(e.value)}
                options={pharmacyGroupOptions}
                optionLabel='name'
                optionValue='id'
                filter
                disabled={accessLoading}
                placeholder='Select Pharmacy Groups'
                maxSelectedLabels={3}
                style={{ width: '100%' }}
              />
              <small className='p-text-secondary'>
                Every pharmacy in a selected group is enabled automatically.
              </small>
            </div>

            {selectedPharmacies.length === 0 &&
              selectedPharmacyGroups.length === 0 && (
                <small className='p-error'>
                  No pharmacy is enabled yet, so this manufacturer is hidden
                  from everyone.
                </small>
              )}
          </>
        )}

        <div className='field mt-4'>
          <label htmlFor='minimumOrderPrice' className='font-bold'>
            Minimum Order Price
          </label>

          <InputNumber
            inputId='minimumOrderPrice'
            value={manufacturers.gpMinOrderPrice}
            required={!isManufactureEdit}
            onChange={(e) => {
              onInputNumberChange(e, 'gpMinOrderPrice')
            }}
            mode='decimal'
            placeholder='Enter Minimum Order Price'
            showButtons
            min={0}
          />
          {submitted && !manufacturers.gpMinOrderPrice && (
            <small className='p-error'>Minimum Order Price is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='email' className='font-bold'>
            Email
          </label>
          <InputText
            id='email'
            type='email'
            value={manufacturers.email}
            required={!isManufactureEdit}
            onChange={(e) => onInputChange(e, 'email')}
            placeholder={
              isManufactureEdit
                ? `${manufacturers.email}`
                : 'Enter Email Address'
            }
            className={classNames('w-full md:w-14rem')}
          />
          {submitted && !manufacturers.email && (
            <small className='p-error'>email is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='email' className='font-bold'>
            Email Domain
          </label>
          <InputText
            id='emailDomain'
            type='text'
            value={manufacturers.emailDomain}
            onChange={(e) => onInputChange(e, 'emailDomain')}
            placeholder='Enter Email Domain (optional)'
            className={classNames('w-full md:w-14rem')}
          />
        </div>
        <div className='field mt-4'>
          <label htmlFor='leadTime' className='font-bold'>
            Lead Time In Days
          </label>
          <InputNumber
            inputId='minmax-buttons'
            value={manufacturers?.leadTime}
            required={!isManufactureEdit}
            onChange={(e) => {
              onInputNumberChange(e, 'leadTime')
            }}
            mode='decimal'
            placeholder='Lead Time'
            showButtons
            min={0}
            max={10}
          />
          {submitted && !manufacturers?.leadTime && (
            <small className='p-error'>Lead time is required.</small>
          )}
        </div>

        <div className='field mt-4'>
          <label htmlFor='calendar-timeonly' className='font-bold block mb-2'>
            Cutoff Time
          </label>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div>
              <TimePicker
                id='calendar-timeonly'
                value={
                  manufacturers.cutOffTime
                    ? dayjs(manufacturers.cutOffTime)
                    : null
                }
                onChange={(selectedTime) => {
                  const formattedTime = selectedTime.valueOf()
                  // ? dayjs(selectedTime).format('h:mm A')
                  // : // : dayjs(manufacturers.cutOffTime)
                  //   dayjs(manufacturers.cutOffTime)
                  setManufacturers({
                    ...manufacturers,
                    cutOffTime: formattedTime,
                  })
                  // onInputChange(formattedTime, 'cutOffTime')
                  // onInputChange(
                  //   { target: { value: formattedTime } },
                  //   'cutOffTime'
                  // )
                }}
              />
            </div>
          </LocalizationProvider>
          {submitted && !manufacturers.cutOffTime && (
            <small className='p-error'>Cutoff time is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='shippingFee' className='font-bold block mb-2'>
            Shipping Fee
          </label>
          <div>
            <InputNumber
              inputId='shippingFee'
              value={manufacturers?.shippingFee}
              required={!isManufactureEdit}
              onChange={(e) => {
                onInputNumberChange(e, 'shippingFee')
              }}
              mode='decimal'
              placeholder='Shipping Fee'
              showButtons
              minFractionDigits={0}
              maxFractionDigits={2}
            />
          </div>

          {submitted && (manufacturers.shippingFee === null || manufacturers.shippingFee === undefined) && (
            <small className='p-error'>Shipping fee is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='odPaAdvantage' className='font-bold block mb-2'>
            OD PA Advantage (%)
          </label>
          <div>
            <InputNumber
              inputId='odPaAdvantage'
              value={manufacturers?.odPaAdvantage || 0}
              onChange={(e) => {
                onInputNumberChange(e, 'odPaAdvantage')
              }}
              mode='decimal'
              placeholder='OD PA Advantage'
              showButtons
              min={0}
              minFractionDigits={0}
              maxFractionDigits={2}
            />
          </div>
          <small className='p-text-secondary'>
            Extra PA percentage added to OriginsDirect/OriginsInstant orders. Leave as 0 for no advantage.
          </small>
        </div>
        <div className='field mt-4'>
          <label htmlFor='systems' className='font-bold block mb-2'>
            Systems
          </label>
          <div className='d-flex gap-4  '>
            <div className='p-field-checkbox d-flex   gap-1'>
              <div>
                <Checkbox
                  inputId='originsDirect'
                  value='Origins Direct'
                  onChange={onSystemChange}
                  checked={selectedSystems.includes('Origins Direct')}
                />
              </div>
              <div>
                <label htmlFor='originsDirect' className='ml-2'>
                  Origins Direct
                </label>
              </div>
            </div>
            <div className='p-field-checkbox d-flex gap-1'>
              <div>
                <Checkbox
                  inputId='originsDirectInstant'
                  value='Origins Direct Instant'
                  onChange={onSystemChange}
                  checked={selectedSystems.includes('Origins Direct Instant')}
                />
              </div>
              <div>
                <label htmlFor='originsDirectInstant' className='ml-2 '>
                  Origins Direct Instant
                </label>
              </div>
            </div>
          </div>

          {submitted && !manufacturers.systems && (
            <small className='p-error'>Systems is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <div className='p-field-checkbox d-flex gap-2 align-items-center'>
            <Checkbox
              inputId='disableRequestNewAccount'
              checked={!!manufacturers.disableRequestNewAccount}
              onChange={(e) =>
                setManufacturers({
                  ...manufacturers,
                  disableRequestNewAccount: e.checked,
                })
              }
            />
            <label htmlFor='disableRequestNewAccount' className='mb-0'>
              Disable requesting a new account
            </label>
          </div>
        </div>
      </Dialog>
    </>
  )
})

export default AddEditManufacturer
